import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactElement, ReactNode } from "react";
import { cn } from "../lib/cn";

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  /** Plain text. A tooltip is never the only place information exists. */
  content: ReactNode;
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  /** Set when the trigger is disabled: Radix needs a focusable wrapper. */
  wrapDisabled?: boolean;
}

/**
 * (H) A tooltip is never the only place information exists (doc 40 §14) — it
 * repeats or expands a label, it does not replace one.
 *
 * `content` may be nullish, in which case the trigger is returned bare. That
 * is not laziness: the collapsed sidebar needs label tooltips and the expanded
 * one must not have them, and a component that disappears cleanly makes that
 * one line at the call site instead of a conditional wrapper.
 */
export function Tooltip({ content, children, side = "top", wrapDisabled = false }: TooltipProps) {
  if (content === undefined || content === null || content === false || content === "") {
    return children;
  }
  return (
    <TooltipPrimitive.Root>
      {/* Always `asChild`: wrapping a disabled control in the Radix trigger's own
          <button> nested one interactive element inside another (axe). */}
      <TooltipPrimitive.Trigger asChild>
        {wrapDisabled ? <span tabIndex={0}>{children}</span> : children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className={cn(
            "z-tooltip max-w-xs rounded-sm bg-inverted px-2 py-1 text-xs text-on-inverted shadow-tooltip",
            "data-[state=delayed-open]:animate-fade-in motion-reduce:animate-none",
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
